import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useReducer, useState } from 'react'
import ButtonLoader from '../components/ButtonLoader'
import { ErrorMessage } from '../components/ErrorMessage'
import { TermsModal } from '../components/TermsModal'
import DI from '../di-container'
import { PHONE_NUMBER_LENGTH } from '@/utils/constants'
import { AxiosError } from 'axios'
import { getErrorMessage } from '@/services/utils'
import { useSession } from 'next-auth/react'

type LoginFormState = {
    phoneNumber: string
    message: string
    loading: boolean
    reCapturetoken: string
    isreCaptureMounted: boolean
}

const Login = () => {
    const router = useRouter()
    const [form, setForm] = useReducer((state: LoginFormState, newState: Partial<LoginFormState>) => ({ ...state, ...newState }), {
        message: '',
        phoneNumber: '',
        reCapturetoken: '',
        loading: false,
        isreCaptureMounted: false,
    })
    const { status } = useSession()

    const [reCapturetoken, setReCapturetoken] = useState<string>('')

    const onRecaptureCompleted = (token: string) => {
        setReCapturetoken(token)
    }

    let retries = 0

    const initRecaptcha = () => {
        retries++
        if (form.isreCaptureMounted || retries >= 20) return
        try {
            //@ts-ignore
            grecaptcha.render('recaptcha-container', {
                sitekey: process.env.NEXT_PUBLIC_RECAPTCHA_KEY,
                callback: onRecaptureCompleted,
            })

            setForm({
                isreCaptureMounted: true,
            })

            return
        } catch (error) { }

        window.setTimeout(initRecaptcha, 1000)
    }

    useEffect(() => {
        initRecaptcha()
    }, [])

    useEffect(() => {
        if (status === 'authenticated') {
            router.push('/dashboard')
        }
    }, [status])

    useEffect(() => {
        if (form.message) {
            //@ts-ignore
            grecaptcha.reset()
            setForm({
                message: '',
                reCapturetoken: '',
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.phoneNumber])

    const isPhoneNumberValid = () => {
        const reg = /^[0-9\b]{10}$/
        if (reg.test(form.phoneNumber)) return true
        return false
    }

    const isformValid = () => {
        return isPhoneNumberValid() && reCapturetoken.length
    }

    const sendOtp = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!isformValid) {
            return
        }

        setForm({
            loading: true,
        })

        try {
            const res = await DI.authService
                .sendOTP(form.phoneNumber, reCapturetoken)

            if (res.code === '200') {
                router.push('/verify')
                return
            }
        } catch (error) {
            setForm({
                message: getErrorMessage(error as AxiosError),
                loading: false,
            })
        }
    }

    return (
        <>
            <Head>
                <title>ECG PowerApp | Pay for your prepaid and postpaid electricity</title>
                <meta name='viewport' content='width=device-width, initial-scale=1.0' />
                <meta
                    name='description'
                    content='We have refreshed how you add your meter to pay for your prepaid and postpaid electricity, and also simplified the payment process.'
                />
                <meta
                    name='keywords'
                    content=' ECG App, ECG PowerApp, pay electricity online, ECG prepaid app, ECG app download'
                />
                <link rel='canonical' href='https://powerapp.ecg.com.gh' />
            </Head>
            <div className='vh-100'>
                <div className='row h-100 align-items-center justify-content-center g-0 bg-light'>
                    <div className='col'>
                        <div>
                            <div className='row w-100 justify-content-center g-0'>
                                <div className='col-11 col-md-5 col-lg-5 col-xl-3'>
                                    <div className='bg-white border-rounded p-4 pt-5 shadow-sm border'>
                                        <div className='d-flex justify-content-center mb-4 pt-3'>
                                            <Image
                                                src='/images/logo.png'
                                                className=''
                                                width='60'
                                                alt='ECG logo'
                                                height='60'
                                            />
                                        </div>
                                        <form className='login-step-one' onSubmit={sendOtp}>
                                            <div className='text-center mb-4'>
                                                <h6 className='text-black fw-bold'>
                                                    Login with your Ghana mobile number
                                                </h6>
                                                <p className='text-muted'>
                                                    Your mobile number may be verified against your Ghana Card details
                                                </p>
                                            </div>
                                            {form.message ? <ErrorMessage message={form.message} /> : null}
                                            <div className='mb-4'>
                                                <input
                                                    onChange={e => {
                                                        setForm({
                                                            phoneNumber: e.target.value,
                                                        })
                                                    }}
                                                    type='text'
                                                    maxLength={PHONE_NUMBER_LENGTH}
                                                    className='form-control'
                                                    inputMode='numeric'
                                                    placeholder='0244123456'
                                                />
                                            </div>

                                            <div className='d-flex justify-content-center mb-4'>
                                                <div id='recaptcha-container'></div>
                                            </div>
                                            {form.loading ? (
                                                <button disabled className='btn btn-primary w-100'>
                                                    <ButtonLoader />
                                                </button>
                                            ) : (
                                                <button
                                                    disabled={!isformValid()}
                                                    className='btn btn-primary w-100'
                                                    type='submit'>
                                                    Login
                                                </button>
                                            )}

                                            <p className='small text-center mt-3 mb-0 text-muted'>
                                                By signing up you agree to our
                                                <TermsModal text=' Terms of Use and Privacy Policy ' />
                                            </p>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Login
